import { useEffect, useRef, useState } from 'react'
import QuestCard, { type QuestPhase } from '../QuestCard'
import { getQuestsForGameweek } from '../../quests'
import type { QuestResult } from '../../questResults'
import { prefersReducedMotion } from '../../scoreCountUp'
import {
  buildQuestPick,
  buildQuestResult,
  hasQuestPickValue,
  questResultIsShowable,
} from './questCardProps'
import type { Game, QuestId, QuestPickState } from '../../types'

// Weekly Quests reveal — once
// every game card's own reveal cascade has finished, the quest pair reveals
// with its own stagger rather than simultaneously. Matches
// docs/QuestReveal.jsx's mock timing (q1 at 900ms, q2 at 1700ms off a shared
// start — an 800ms gap between cards).
const QUEST_REVEAL_STAGGER_MS = 800

interface GameweekQuestsProps {
  gameweek: number
  games: Game[]
  /** This gameweek's weekly quest picks, keyed by the 2 quest ids the
   *  round-robin schedule surfaced — see quests.ts. */
  questPicks: Partial<Record<QuestId, QuestPickState>>
  /** The gameweek's graded quest truth, keyed by quest id. A quest absent from
   *  this map has not been graded yet — the round is still finishing, or its post-match detail
   *  sync has not landed — and its card stays sealed rather than showing a verdict. */
  questResults: Partial<Record<QuestId, QuestResult>>
  /** Which quests this deployment runs. Fewer than 2 — including the
   *  `none` shelf switch and the not-yet-loaded case — means no section at all: no header,
   *  no cards, no pick UI, no result state. */
  questsEnabled: QuestId[]
  /** The gate from the grid: data settled AND the game-card reveal cascade finished. */
  questsRevealed: boolean
  /** The same gate asked of the fixtures settled *so far* — what a half-time
   *  quest reveals on, since it is decided long before the round's last whistle. */
  questsEarlyRevealed?: boolean
  /** Whether the user has started this gameweek — gates the section's
   *  visibility; `allPredicted` then drives its dimmed-vs-unlocked look once shown. */
  hasStartedGw: boolean
  allPredicted: boolean
  deadlinePassed: boolean
  onPickQuestTeam: (questId: QuestId, team: string) => void
  onPickQuestMatch: (questId: QuestId, game: Game) => void
  onSelectQuestSide: (questId: QuestId, side: 'over' | 'under') => void
  /** Where the quest cards sit in the entry ignite wave — the
   *  fixture count, so the hue keeps stepping across the whole page rather
   *  than restarting on the quest row. */
  igniteBaseIndex?: number
  /** True for the ~590ms the quest row is flashing. Each card still joins only
   *  if it is in `confirm`. */
  igniteFiring?: boolean
}

/** Full-width Weekly Quests section — rendered directly below the fixture
 *  grid. Placement relative to the grid/Banker bar is unresolved; this is the
 *  least-disruptive default, not a design call. Hidden entirely until the
 *  gameweek is started — a fresh gameweek shows the hero alone, no quest
 *  cards and no header. Split out of GameweekGrid.tsx. */
function GameweekQuests({
  gameweek,
  games,
  questPicks,
  questResults,
  questsEnabled,
  questsRevealed,
  questsEarlyRevealed = false,
  hasStartedGw,
  allPredicted,
  deadlinePassed,
  onPickQuestTeam,
  onPickQuestMatch,
  onSelectQuestSide,
  igniteBaseIndex = 0,
  igniteFiring = false,
}: GameweekQuestsProps) {
  // The 2 quests the round-robin schedule surfaces for this gameweek, each rendered as a
  // real QuestCard reusing the shared fixture deadline (no separate quest clock, per
  // the Open Questions).
  const questsThisGw = getQuestsForGameweek(gameweek, questsEnabled)

  // Quest-pair stagger ("Motion" section, timing reference
 // docs/QuestReveal.jsx): once the gate above opens, each quest card lands
  // on its result in turn rather than simultaneously. `questsRevealedCount`
  // is how many (in quest order) should currently render as revealed;
  // ratchets up on a timer per card and resets the moment the gate closes
  // (e.g. dev "Clear seen") so a replay restages the same stagger.
  const [questsRevealedCount, setQuestsRevealedCount] = useState(0)
  const questsRevealStartedRef = useRef(false)
  // Either gate opens the stagger: the round's own (every fixture settled and revealed) or the
  // early one a half-time quest lands on mid-round. Which quests actually flip to
  // a result is then decided per card by whether a verdict exists for this player.
  const revealGateOpen = questsRevealed || questsEarlyRevealed
  useEffect(() => {
    if (!revealGateOpen) {
      questsRevealStartedRef.current = false
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQuestsRevealedCount(0)
      return
    }
    if (questsRevealStartedRef.current) return
    questsRevealStartedRef.current = true

    if (prefersReducedMotion()) {
      // Reduced motion: no stagger — every quest card crossfades to its
      // final state at once (still gated on the cascade above).
      setQuestsRevealedCount(questsThisGw.length)
      return
    }
    const timers = questsThisGw.map((_, index) =>
      setTimeout(
        () => setQuestsRevealedCount((count) => Math.max(count, index + 1)),
        index * QUEST_REVEAL_STAGGER_MS
      )
    )
    return () => timers.forEach(clearTimeout)
    // questsThisGw isn't a dependency: getQuestsForGameweek(gameweek) returns a
    // fresh array identity every render (same underlying quest objects), so
    // depending on it would refire this effect every render. `gameweek` is
    // the actual input it varies with; `.length` is 2 whenever quests run at
    // all and 0 when they are disabled, never anything else.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealGateOpen, gameweek, questsThisGw.length])

  // Quests off for this deployment: the section is *absent*, not a greyed-out
  // card and not "coming soon". Checked after the hooks above so the order stays stable.
  if (questsThisGw.length === 0) return null
  if (!hasStartedGw) return null

  const questCards = questsThisGw.map((quest, index) => {
    const state = questPicks[quest.id]

    // The graded result is a second gate on top of the reveal timing: a gameweek
    // can be over on screen while its quests are still ungraded — tier 2 and 3 wait on the
    // post-match detail sync. Until the result exists the card stays sealed, because "no verdict
    // yet" and "you scored nothing" are different messages. A half-time quest graded over only
    // part of the round has a result without having this player's verdict in it, which is the
    // other half of the same rule.
    const questResult = questResults[quest.id]
    const showResult = questResult !== undefined && questResultIsShowable(quest, questResult, state)
    if (revealGateOpen && index < questsRevealedCount && showResult) {
      const { pick, result } = buildQuestResult(quest, games, state, questResult)
      return (
        <QuestCard
          key={quest.id}
          phase="result"
          question={quest.question}
          pick={pick}
          result={result}
        />
      )
    }

    // Lock is driven purely by the shared gameweek deadline, for every quest
    // type — matching GameCard's isSealed = deadlinePassed, never merely
    // because a pick was made. A made-but-unlocked pick renders as 'confirm'
    // (stops the cta pump/particles, shows the filled/editable pick) with no
    // separate confirm step to take. Before every fixture is predicted,
    // the card instead renders 'dimmed' — a distinct, inert
    // pre-start state — regardless of deadlinePassed's usual precedence,
    // since a still-open gameweek that hasn't been fully predicted can't
    // have reached its deadline anyway. While games are settled but the
    // reveal cascade hasn't finished, deadlinePassed is
    // already true, so the quest card naturally stays 'locked' — the sealed
    // presentation — until `questsRevealed` flips this branch instead.
    const phase: QuestPhase = deadlinePassed
      ? 'locked'
      : !allPredicted
        ? 'dimmed'
        : hasQuestPickValue(quest, state)
          ? 'confirm'
          : 'cta'
    return (
      <QuestCard
        key={quest.id}
        phase={phase}
        questTag={quest.tag}
        question={quest.question}
        pick={buildQuestPick(quest, games, state)}
        gameweekGames={games}
        deadlinePassed={deadlinePassed}
        onSelectSide={(side) => onSelectQuestSide(quest.id, side)}
        onPickTeam={(team) => onPickQuestTeam(quest.id, team)}
        onPickMatch={(game) => onPickQuestMatch(quest.id, game)}
        igniteIndex={igniteBaseIndex + index}
        igniteFiring={igniteFiring}
      />
    )
  })

  return (
    <div className="gameweek-grid__quests">
      <h2 className="gameweek-grid__quests-header heading-section">Weekly Quests</h2>
      <div className="gameweek-grid__quests-stack">{questCards}</div>
    </div>
  )
}

export default GameweekQuests
